// ── SideNav — боковая панель на компьютере и планшете ────────────────────────
// Как на макете: логотип, разделы (Главная · Повторение · Чтение · Статистика),
// внизу — «+ Новый» и состояние синхронизации. Аккаунт — в шапке (Chrome.js).
// Ширина по стандарту Material: от 1200 px — панель с подписями справа от значков,
// от 768 до 1200 — узкая полоса (значок и подпись под ним). Ширина публикуется в
// CSS-переменной --ac-side-w: страница сдвигается вправо, мини-плеер — тоже.

import { useEffect, useState } from 'react';
import { SANS, themeKit } from './themes';
import Icon from './Icon';
import { Brand } from './Chrome';
import { outboxSize } from './outbox';

import DueCount, { dueLabel } from './ui/DueCount';
import { t } from './i18n';
import Button from './ui/Button';
import IconButton from './ui/IconButton';
import { panelSurface } from './ui/surface';
import { useMedia } from './ui/theme';
import WordSlots from './components/WordSlots';
import DeckWidgets from './components/DeckWidgets';
export const SIDE_W = 232;
export const RAIL_W = 84;

// badge — готовый элемент (например, DueCount), badgeLabel — его текст для экранного диктора.
function NavItem({ C, icon, label, active, badge, badgeLabel, onClick, compact }) {
  const [hover, setHover] = useState(false);
  if (compact) {
    return (
      <button onClick={onClick} aria-current={active ? 'page' : undefined} title={label}
        aria-label={badgeLabel ? `${label}. ${badgeLabel}` : label}
        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 4, padding: '4px 0', width: '100%', fontFamily: SANS, color: active ? C.text : C.mid }}>
        <span style={{ position: 'relative', width: 56, height: 32, borderRadius: 16,
          background: active ? C.bluePale : hover ? C.surface : 'transparent', color: active ? C.blueDark : C.mid,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon n={icon} s={20} sw={active ? 2.1 : 1.8} />
          {badge && <span style={{ position: 'absolute', top: -6, left: 30, display: 'inline-flex' }}>{badge}</span>}
        </span>
        <span style={{ fontSize: 11.5, fontWeight: active ? 600 : 500 }}>{label}</span>
      </button>
    );
  }
  return (
    <button onClick={onClick} aria-current={active ? 'page' : undefined} aria-label={badgeLabel ? `${label}. ${badgeLabel}` : undefined}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} data-panel={active ? '' : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 40, padding: '0 10px',
        ...(active ? panelSurface(C) : { background: hover ? C.surface : 'transparent', border: '1px solid transparent' }),
        borderRadius: 8, cursor: 'pointer', fontFamily: SANS, fontSize: 14, fontWeight: active ? 600 : 500,
        color: active ? C.text : C.mid, textAlign: 'left' }}>
      <span style={{ display: 'inline-flex', color: active ? C.blueDark : C.mid }}><Icon n={icon} s={18} sw={active ? 2.1 : 1.8} /></span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {badge && <span style={{ fontSize: 13, flexShrink: 0, display: 'inline-flex' }}>{badge}</span>}
    </button>
  );
}

// Состояние синхронизации — словами, а не цветом безымянной кнопки.
export function syncState({ syncing, syncResult, syncErrorDetail }) {
  const pending = outboxSize();
  if (syncing) return { icon: 'refresh', spin: true, text: t("Синхронизация…"), tone: 'mid' };
  if (syncResult === 'error') return { icon: 'alert', text: t("Не синхронизировано"), tone: 'danger', hint: syncErrorDetail };
  if (syncResult === 'no_tables') return { icon: 'alert', text: t("Нужна настройка базы"), tone: 'warm' };
  if (pending > 0) return { icon: 'refresh', text: t("В очереди: {0}",[pending]), tone: 'warm', hint: t("Изменения сохранены на устройстве и отправятся сами") };
  return { icon: 'check', text: t("Синхронизировано"), tone: 'green' };
}

export default function SideNav({ pal: C, compact, active, due = { repeat: 0, fresh: 0 }, onHome, onAudio, onReview, onRead, onStats, onNew,
  onSync, syncing, syncResult, syncErrorDetail, words, todayCards, onStartReview }) {
  const width = compact ? RAIL_W : SIDE_W;
  // «Аркада» на высоком экране: в пустом месте меню — игровой автомат со словами.
  // «Колода» (layout hand): в меню — сводка дня (DeckWidgets); на главной «Новый» — у библиотеки
  // (как на макете), на остальных экранах — здесь, как обычно.
  const hand = themeKit(C).layout === 'hand';
  const tall = useMedia('(min-height: 720px)');
  const slots = !hand && !compact && tall && C.face && C.lift && words?.length > 0;

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--ac-side-w', `${width}px`);
    const prev = document.body.style.paddingLeft;
    document.body.style.paddingLeft = `${width}px`;
    return () => { root.removeProperty('--ac-side-w'); document.body.style.paddingLeft = prev; };
  }, [width]);

  const sync = syncState({ syncing, syncResult, syncErrorDetail });
  const toneColor = { mid: C.mid, danger: C.danger, warm: C.warmDark, green: C.green }[sync.tone];

  return (
    <nav aria-label={t("Разделы")} data-surface="glass" style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width, zIndex: 90, boxSizing: 'border-box',
      // «Аркада»: стекло, в нём виден стол; плоские темы — сплошной фон.
      background: C.lift ? C.glassHdr : C.pageBg2, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column',
      padding: '12px', paddingLeft: compact ? 6 : 12, paddingRight: compact ? 6 : 12, gap: compact ? 10 : 2, fontFamily: SANS }}>

      <button onClick={onHome} title={t("AntonCards — главная")}
        style={{ display: 'flex', alignItems: 'center', justifyContent: compact ? 'center' : 'flex-start', gap: 10, height: 44, flexShrink: 0,
          background: 'none', border: 'none', cursor: 'pointer', padding: compact ? 0 : '0 6px', margin: compact ? '0 0 4px' : '0 0 12px',
          fontFamily: SANS, fontSize: 16, fontWeight: 600, color: C.text, letterSpacing: '-0.01em' }}>
        <Brand C={C} size={28} />{!compact && 'AntonCards'}
      </button>

      {/* Две главные части — аудио и чтение, потом повторение и статистика */}
      <NavItem C={C} compact={compact} icon="home" label={t("Главная")} active={active === 'home'} onClick={onHome} />
      <NavItem C={C} compact={compact} icon="headphones" label={t("Аудио")} active={active === 'audio'} onClick={onAudio} />
      <NavItem C={C} compact={compact} icon="book" label={t("Чтение")} active={active === 'read'} onClick={onRead} />
      <NavItem C={C} compact={compact} icon="cards" label={t("Повторение")} active={active === 'review'} onClick={onReview}
        badge={<DueCount C={C} repeat={due.repeat} fresh={due.fresh} variant={compact ? 'pill' : 'text'} />}
        badgeLabel={due.repeat + due.fresh > 0 ? dueLabel(due.repeat, due.fresh) : ''} />
      <NavItem C={C} compact={compact} icon="chart" label={t("Статистика")} active={active === 'stats'} onClick={onStats} />

      {slots && <WordSlots C={C} words={words} />}
      {hand && !compact && <DeckWidgets C={C} cards={todayCards} onStart={onStartReview} />}
      <div style={{ marginTop: 'auto', paddingTop: hand && !compact ? 12 : 0, display: 'flex', flexDirection: 'column', gap: compact ? 10 : 8 }}>
        {hand && !compact && active === 'home' ? null : compact ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, fontFamily: SANS, color: C.mid }}>
            <IconButton C={C} variant="filled" size={48} label={t("Создать")} onClick={onNew} style={{ borderRadius: 12 }}>
              <Icon n="plus" s={22} sw={2.2} />
            </IconButton>
            <span aria-hidden="true" style={{ fontSize: 11.5, fontWeight: 600 }}>{t("Новый")}</span>
          </div>
        ) : (
          <Button C={C} variant="primary" icon="plus" full onClick={onNew}>{t("Новый")}</Button>
        )}
        <button onClick={onSync} disabled={syncing} title={sync.hint || t("Синхронизировать сейчас")}
          style={{ display: 'flex', alignItems: 'center', justifyContent: compact ? 'center' : 'flex-start',
            flexDirection: compact ? 'column' : 'row', gap: compact ? 4 : 8, width: '100%', minHeight: 36,
            padding: compact ? '4px 0' : '0 6px', background: 'none', border: 'none', borderRadius: 8,
            cursor: syncing ? 'default' : 'pointer', fontFamily: SANS, fontSize: compact ? 11 : 13, color: toneColor, textAlign: 'left' }}>
          <span style={{ display: 'inline-flex', animation: sync.spin ? 'spin 0.8s linear infinite' : 'none' }}><Icon n={sync.icon} s={compact ? 18 : 16} /></span>
          <span style={{ lineHeight: 1.2, color: sync.tone === 'green' ? C.mid : toneColor }}>
            {compact ? (sync.tone === 'green' ? t("Синхр.") : sync.tone === 'danger' ? t("Ошибка") : sync.spin ? '…' : t("Очередь")) : sync.text}
          </span>
        </button>
        {hand && !compact && <div style={{ fontSize: 12, color: C.faint, padding: '0 6px', lineHeight: 1.4 }}>{t("Каждая карточка — в твою пользу.")}</div>}
      </div>
    </nav>
  );
}
